import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { createDynamicCSS } from "../../../../utils/CreateDynamicCSS";
import { fetchMemberProfileCard } from "../memberProfileCardApi";
import defaultAvatarImg from "../../../medias/avatar_default_medium.jpg";
import DonorGemBadge from "../../donate/DonorGemBadge";
import { normalizeDonorGem } from "../../../utils/donorGemDisplay";
import {
  resolveMemberHeroRingDisplay,
  sanitizeMemberHeroRingHex,
} from "../../../utils/memberHeroRingColor";
import { resolveProfileThemeEffects, resolveVideoDarken } from "../../../utils/memberProfileThemeDisplay";
import DonorShootingStar from "./DonorShootingStar";
import PerkThemeHaloLayer from "./PerkThemeHaloLayer";
import PerkThemeMediaLayer from "./PerkThemeMediaLayer";
import PerkThemeOverlayEffects from "./PerkThemeOverlayEffects";
import PerkThemeUnderlayEffects from "./PerkThemeUnderlayEffects";

const posClassCache = new Set();

function monikerText(card) {
  if (!card) return null;
  if (card.monikerRedacted) {
    return card.monikerRedactedKind === "redacted" ? "Redacted" : "Hidden";
  }
  if (card.moniker != null && String(card.moniker).trim()) {
    return String(card.moniker).trim();
  }
  return null;
}

/** Deepest org unit icon: section > branch > division. Executive / empty → null. */
function resolveOrgUnitIconUrl(card) {
  if (!card) return null;

  const division = card.division && String(card.division).trim();
  const branch = card.branch && String(card.branch).trim();
  const section = card.section && String(card.section).trim();
  if (!division && !branch && !section) return null;

  const divisionImageUrl =
    card.divisionImageUrl && String(card.divisionImageUrl).trim()
      ? String(card.divisionImageUrl).trim()
      : "";
  const branchImageUrl =
    card.branchImageUrl && String(card.branchImageUrl).trim()
      ? String(card.branchImageUrl).trim()
      : "";
  const sectionImageUrl =
    card.sectionImageUrl && String(card.sectionImageUrl).trim()
      ? String(card.sectionImageUrl).trim()
      : "";

  if (division && branch && section) return sectionImageUrl || null;
  if (division && branch) return branchImageUrl || null;
  if (division) return divisionImageUrl || null;
  return null;
}

function positionClassForPopover(left, top) {
  const l = Math.round(left);
  const t = Math.round(top);
  const cls = `member-profile-card-pos-${t}-${l}`;
  if (posClassCache.has(cls)) return cls;
  posClassCache.add(cls);
  return createDynamicCSS(cls, {
    position: "fixed",
    left: `${l}px`,
    top: `${t}px`,
    zIndex: 12040
  });
}

function MemberProfileCardBody({ card, preview, loading, error }) {
  const c = card || {};
  const discordName =
    (c.discordName && String(c.discordName).trim()) ||
    (preview?.discordName && String(preview.discordName).trim()) ||
    "";
  const discordAvatar = c.discordAvatar || preview?.discordAvatar || null;
  const handleStr =
    (c.primaryHandle && String(c.primaryHandle).trim()) ||
    (preview?.handle != null ? String(preview.handle).trim() : "") ||
    "";

  const onAvatarError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = defaultAvatarImg;
  };

  const groupNames =
    card && Array.isArray(card.groupDisplayNames)
      ? card.groupDisplayNames.map((n) => String(n).trim()).filter(Boolean)
      : [];

  const rankName =
    card?.rankName != null && String(card.rankName).trim()
      ? String(card.rankName).trim()
      : "";
  const payGrade =
    card?.payGrade != null && String(card.payGrade).trim()
      ? String(card.payGrade).trim()
      : "";

  const unrankedLabel =
    card && card.unrankedKind === "guest"
      ? "Guest"
      : card && card.unrankedKind === "former"
        ? "Former member"
        : "";

  const assignmentStr =
    card && card.rosterAssignmentDisplay && String(card.rosterAssignmentDisplay).trim()
      ? String(card.rosterAssignmentDisplay).trim()
      : "";

  const divisionStr =
    card && card.division && String(card.division).trim()
      ? String(card.division).trim()
      : "";

  const branchStr =
    card && card.branch && String(card.branch).trim()
      ? String(card.branch).trim()
      : "";

  const sectionStr =
    card && card.section && String(card.section).trim()
      ? String(card.section).trim()
      : "";

  const monikerStr = card ? monikerText(card) : null;

  const showHeroText = Boolean(discordName || handleStr);
  const donorGem = normalizeDonorGem(card);
  const orgUnitIconUrl = resolveOrgUnitIconUrl(card);
  const hasCustomHeroRing = Boolean(sanitizeMemberHeroRingHex(card?.dashboardHeroRingColor));
  const showHeroRing =
    hasCustomHeroRing || Boolean(donorGem?.showBadge && donorGem?.isActive);
  const heroRing = showHeroRing
    ? resolveMemberHeroRingDisplay(card?.dashboardHeroRingColor, {
        showHalo: card?.hasDashboardHeroRingHalo === true,
      })
    : null;

  return (
    <div className="member-profile-card">
      <div className="member-profile-card__hero">
        <div className="member-profile-card__avatar-stack">
          {heroRing?.showHalo ? (
            <div
              className={[
                "member-profile-card__avatar-halo",
                heroRing.haloClass,
              ]
                .filter(Boolean)
                .join(" ")}
              aria-hidden
            />
          ) : null}
          {discordAvatar ? (
            <img
              className="member-profile-card__hero-avatar"
              src={discordAvatar}
              alt=""
              onError={onAvatarError}
            />
          ) : (
            <span
              className="member-profile-card__hero-avatar member-profile-card__hero-avatar--placeholder"
              aria-hidden
            />
          )}
          {heroRing ? (
            <div
              className={[
                "member-profile-card__avatar-ring",
                heroRing.ringBorderClass,
              ]
                .filter(Boolean)
                .join(" ")}
              aria-hidden
            />
          ) : null}
        </div>
        {showHeroText ? (
          <div className="member-profile-card__hero-text">
            {discordName ? (
              <div className="member-profile-card__discord-name">{discordName}</div>
            ) : null}
            {handleStr ? (
              <div className="member-profile-card__handle">{`@${handleStr}`}</div>
            ) : null}
            {donorGem?.showBadge ? (
              <DonorGemBadge
                className="member-profile-card__donor"
                donorGem={donorGem}
                showLabel
              />
            ) : null}
          </div>
        ) : null}
        {orgUnitIconUrl ? (
          <img
            className="member-profile-card__org-icon"
            src={orgUnitIconUrl}
            alt=""
          />
        ) : null}
      </div>

      {loading ? (
        <div className="member-profile-card__status">Loading…</div>
      ) : null}
      {error && !loading ? (
        <div className="member-profile-card__status member-profile-card__status--error">{error}</div>
      ) : null}

      {!loading && !error && card ? (
        <div className="member-profile-card__body">
          {monikerStr ? (
            <div className="member-profile-card__line">{monikerStr}</div>
          ) : null}

          {card.rankImageUrl || rankName || payGrade || unrankedLabel ? (
            <div className="member-profile-card__rank">
              {card.rankImageUrl ? (
                <img className="member-profile-card__rank-img" src={card.rankImageUrl} alt="" />
              ) : null}
              {rankName ? (
                <span className="member-profile-card__rank-label">{rankName}</span>
              ) : unrankedLabel ? (
                <span className="member-profile-card__rank-label member-profile-card__rank-label--unranked">
                  {unrankedLabel}
                </span>
              ) : null}
              {payGrade ? (
                <span className="member-profile-card__pay-grade-chip">{payGrade}</span>
              ) : null}
            </div>
          ) : null}

          {assignmentStr ? (
            <div className="member-profile-card__line member-profile-card__line--accent">
              {assignmentStr}
            </div>
          ) : null}
          {divisionStr ? (
            <div className="member-profile-card__line">{divisionStr}</div>
          ) : null}
          {branchStr ? (
            <div className="member-profile-card__line">{branchStr}</div>
          ) : null}
          {sectionStr ? (
            <div className="member-profile-card__line">{sectionStr}</div>
          ) : null}

          {groupNames.length > 0 ? (
            <div className="member-profile-card__groups">
              {groupNames.map((name) => (
                <span key={name} className="chip chip-group">
                  {name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {card?.isSuperAdmin ? (
        <div className="member-profile-card__super-admin">super admin</div>
      ) : null}
    </div>
  );
}

/**
 * Portal popover; `anchorRef` should point to the clickable avatar wrapper.
 */
export default function MemberProfileCardPopover({
  open,
  anchorRef,
  onClose,
  userId,
  preview
}) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [posClass, setPosClass] = useState("");

  useEffect(() => {
    if (!open || userId == null) return undefined;
    const uid = Number(userId);
    if (!Number.isInteger(uid) || uid < 1) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setCard(null);

    fetchMemberProfileCard(uid)
      .then((data) => {
        if (cancelled) return;
        if (!data?.ok) {
          setError(data?.error || "Unable to load profile.");
          return;
        }
        setCard(data.card || null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Unable to load profile.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const updatePosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el || typeof el.getBoundingClientRect !== "function") {
      setPosClass("");
      return;
    }
    const r = el.getBoundingClientRect();
    const width = 280;
    const margin = 8;
    let left = r.left + r.width / 2 - width / 2;
    left = Math.min(Math.max(left, margin), window.innerWidth - width - margin);
    const estHeight = 320;
    let top = r.bottom + margin;
    if (top + estHeight > window.innerHeight - margin) {
      top = Math.max(margin, r.top - estHeight - margin);
    }
    setPosClass(positionClassForPopover(left, top));
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, updatePosition, card, loading]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const t = e.target;
      const anchor = anchorRef?.current;
      const pop = document.querySelector(".member-profile-card-popover-shell");
      if (anchor && anchor.contains(t)) return;
      if (pop && pop.contains(t)) return;
      onClose?.();
    };
    window.addEventListener("mousedown", onDown, true);
    return () => window.removeEventListener("mousedown", onDown, true);
  }, [open, onClose, anchorRef]);

  if (!open || userId == null) return null;

  const uid = Number(userId);
  if (!Number.isInteger(uid) || uid < 1) return null;

  const themeEffects = resolveProfileThemeEffects(card?.profileTheme);
  const {
    showDonorSpace,
    isPremiumEffect,
    showNebulaMorph,
    showShootingStars,
    overlayEffectKeys,
    effectConfig,
    cardMediaUrl,
    cardMediaType,
  } = themeEffects;

  return createPortal(
    <div
      className={`member-profile-card-popover-shell ${posClass}`.trim()}
      role="dialog"
      aria-label="Member profile"
    >
      <div
        className={[
          "member-profile-card-popover",
          showDonorSpace ? "member-profile-card-popover--donor" : "",
          !showDonorSpace && (cardMediaUrl || overlayEffectKeys?.length)
            ? "member-profile-card-popover--theme-fx"
            : "",
          isPremiumEffect ? "member-profile-card-popover--donor-premium" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <PerkThemeHaloLayer effectConfig={effectConfig} />
        {overlayEffectKeys?.length ? (
          <PerkThemeUnderlayEffects
            effectKeys={overlayEffectKeys}
            effectConfig={effectConfig}
          />
        ) : null}
        {cardMediaUrl ? (
          <PerkThemeMediaLayer
            url={cardMediaUrl}
            mediaType={cardMediaType}
            darken={resolveVideoDarken(
              cardMediaUrl,
              cardMediaType,
              effectConfig?.cardVideoDarken,
            )}
            className="member-profile-card-popover__theme-media"
          />
        ) : null}
        {showNebulaMorph ? (
          <div className="member-profile-card-popover__nebula-morph" aria-hidden />
        ) : null}
        {showShootingStars ? <DonorShootingStar /> : null}
        {overlayEffectKeys?.length ? (
          <PerkThemeOverlayEffects
            effectKeys={overlayEffectKeys}
            mediaUrl={cardMediaUrl}
            mediaType={cardMediaType}
            effectConfig={effectConfig}
          />
        ) : null}
        <MemberProfileCardBody
          card={card}
          preview={preview}
          loading={loading}
          error={error}
        />
      </div>
    </div>,
    document.body
  );
}
